/**
 * @file securityRoleService.js
 * @description Mock service layer for handling operations related to security roles.
 */
import { clientApiHttp, systemHttp } from './httpService';
import qs from 'qs';

const getTenantId = () => +window.localStorage.getItem('tenantId');
const isMultiTenant = () =>
    window.localStorage.getItem('multitenantFlag') === 'true';

/**
 * Fetch all security roles.
 * @returns {Promise<Array>} A promise that resolves to an array of security role objects.
 */
export const fetchSecurityRoles = async isMultiTenant => {
    try {
        let tenantId = isMultiTenant ? 0 : getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/security-role`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching security roles:', error);
        throw error;
    }
};

/**
 * Fetch a security role by ID.
 * @param {number} roleId - The ID of the role to fetch.
 * @returns {Promise<Object>} A promise that resolves to the security role object.
 */
export const fetchSecurityRoleById = async roleId => {
    try {
        let tenantId = isMultiTenant() ? 0 : getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/security-role/${roleId}`,
        );
        const role = response.data;
        if (!role) throw new Error('Role not found');
        return role;
    } catch (error) {
        console.error(`Error fetching role with ID ${roleId}:`, error);
        throw error;
    }
};

/**
 * Delete multiple security roles.
 * @param {Array<number>} roleIds - The IDs of the roles to delete.
 * @returns {Promise<void>}
 */
export const deleteSecurityRoles = async roleIds => {
    try {
        let tenantId = isMultiTenant() ? 0 : getTenantId();
        return Promise.all(
            roleIds.map(roleId => {
                return deleteSecurityRole(tenantId, roleId);
            }),
        );
    } catch (error) {
        console.error('Error deleting security roles:', error);
        throw error;
    }
};

export const deleteSecurityRole = async (tenantId, securityRoleId) => {
    try {
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/security-role/${securityRoleId}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error deleting security role:', error);
        throw error;
    }
};

/**
 * Fetch available security attributes.
 * @returns {Promise<Array>} A promise that resolves to an array of attribute objects.
 */
export const fetchSecurityAttributes = async (
    includeAdminOnly,
    includeNonAdmin = true,
) => {
    try {
        const response = await systemHttp.get(
            `/v2/system/security-attribute?includeAdminOnly=${includeAdminOnly}&includeNonAdmin=${includeNonAdmin}`,
        );

        return response.data.sort(function (a, b) {
            return a.name.localeCompare(b.name);
        });
    } catch (error) {
        console.error('Error fetching security attributes:', error);
        throw error;
    }
};

/**
 * Fetch security attribute groups with their categories.
 * @param {boolean} includeAdminOnly - Whether to include admin-only groups.
 * @param {boolean} includeNonAdmin - Whether to include non-admin groups.
 * @returns {Promise<Array>} A promise that resolves to an array of group objects.
 */
export const fetchSecurityAttributeGroups = async (
    includeAdminOnly,
    includeNonAdmin = true,
) => {
    try {
        const response = await systemHttp.get(
            `/v2/system/security-attribute-group?includeAdminOnly=${includeAdminOnly}&includeNonAdmin=${includeNonAdmin}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching security attribute groups:', error);
        throw error;
    }
};

/**
 * Toggle the enable/disable status of a security role.
 * @param {number} roleId - The ID of the role to toggle.
 */
export const toggleRoleStatus = async roleId => {
    try {
        const role = await fetchSecurityRoleById(roleId);

        if (!role) throw new Error('Role not found');

        role.isActive = !role.isActive;

        await updateSecurityRole(role);
    } catch (error) {
        console.error('Error toggling role status:', error);
        throw error;
    }
};

/**
 * Add a security role
 * @param {Object} roleData - The data of the role to add, including id, name, description, and attributes.
 */
export const createSecurityRole = async roleData => {
    let tenantID = isMultiTenant() ? 0 : getTenantId();
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${tenantID}/security-role`,
            roleData,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error adding security role:', error);
        throw error;
    }
};

/**
 * Update a security role
 * @param {Object} roleData - The data of the role to update, including id, name, description, and attributes.
 */
export const updateSecurityRole = async roleData => {
    let tenantID = isMultiTenant() ? 0 : getTenantId();
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${tenantID}/security-role`,
            roleData,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error updating security role:', error);
        throw error;
    }
};
/**
 * Fetch users for a specific security role.
 * @param {number} roleId - The ID of the role to fetch users for.
 * @returns {Promise<Array>} A promise that resolves to an array of user objects.
 */
export const fetchUsersForRole = async roleId => {
    try {
        let tenantId = isMultiTenant() ? 0 : getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/security-role/${roleId}/user`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching users for role:', error);
        throw error;
    }
};

/**
 * Delete selected user(s) from a role based on user's uniqueId.
 * @param {number} roleId - The ID of the role to delete user from.
 * @param {Array<string>} userIds - The unique IDs of the users to delete.
 * @returns {Promise<void>}
 */
export const deleteUserFromRole = async (roleId, userIds) => {
    try {
        let tenantId = isMultiTenant() ? 0 : getTenantId();
        return Promise.all(
            userIds.map(userId => {
                return deleteSecurityRoleUser(tenantId, roleId, userId);
            }),
        );
    } catch (error) {
        console.error('Error deleting user(s) from role:', error);
        throw error;
    }
};

export const deleteSecurityRoleUser = async (tenantId, roleId, userId) => {
    try {
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantId}/security-role/${roleId}/user/${userId}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error deleting security role:', error);
        throw error;
    }
};

/**
 * Search users based on given criteria.
 * @param {Object} searchCriteria - The criteria to search users by.
 * @returns {Promise<Array>} A promise that resolves to an array of user objects.
 */
export const searchUsers = async (searchCriteria, roleId, multiTenant) => {
    try {
        if (!multiTenant) multiTenant = isMultiTenant();
        const tenantId = multiTenant ? 0 : getTenantId();
        const queryString = qs.stringify(
            { ...searchCriteria, tenantId },
            { arrayFormat: 'repeat' },
        );

        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/security-role/${roleId}/user/search?${queryString}`,
        );

        return response.data;
    } catch (error) {
        console.error('Error searching users:', error);
        throw error;
    }
};

/**
 * Add selected users to a specific security role.
 * @param {number} roleId - The ID of the role to add users to.
 * @param {Array<Object>} newUsers - The new users to add with their details.
 */
export const addUsersToRole = async (roleId, newUsers) => {
    try {
        let tenantID = isMultiTenant() ? 0 : getTenantId();
        const response = await clientApiHttp.post(
            `/v2/client/${tenantID}/security-role/${roleId}/user`,
            newUsers,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error adding users to role:', error);
        throw error;
    }
};
